// KillFeed — small top-right ticker showing the last few kills in the
// match. Each row reads "<killer> ➜ <victim archetype>" with team
// colouring so the eye can scan recent action without watching the
// world. Driven by KillEvents (snapshot-diff source), not by lifecycle
// events.
//
// Rows fade out after FADE_AFTER_MS. The DOM list is capped at
// MAX_ROWS so a wipe of six squads doesn't grow the panel indefinitely.

import type { PlayerId } from '@signalclash/shared';

import type { KillEvent } from '../game/KillEvents.js';

const ROOT_ID = 'kill-feed';
const STYLE_ID = 'kill-feed-styles';
const MAX_ROWS = 5;
const FADE_AFTER_MS = 6000;

const ARCHETYPE_LABEL: Record<'recon' | 'assault' | 'engineer' | 'shield', string> = {
  recon: 'Recon',
  assault: 'Assault',
  engineer: 'Engineer',
  shield: 'Shield',
};

interface Row {
  el: HTMLDivElement;
  timer: ReturnType<typeof setTimeout>;
}

export class KillFeed {
  private root: HTMLDivElement | null = null;
  private viewerId: PlayerId;
  private rows: Row[] = [];

  constructor(viewerId: PlayerId) {
    this.viewerId = viewerId;
    injectStyles();
    this.root = ensureRoot();
  }

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
  }

  handleKill(e: KillEvent): void {
    if (this.root === null) return;
    const killerSelf = e.killerOwnerId === this.viewerId;
    const row = document.createElement('div');
    row.className = `kill-feed__row ${killerSelf ? 'kill-feed__row--ally' : 'kill-feed__row--enemy'}`;
    const killer = document.createElement('span');
    killer.className = 'kill-feed__name';
    killer.textContent = killerSelf ? 'You' : 'Enemy';
    const arrow = document.createElement('span');
    arrow.className = 'kill-feed__arrow';
    arrow.textContent = '►';
    const victim = document.createElement('span');
    victim.className = 'kill-feed__name';
    victim.textContent = ARCHETYPE_LABEL[e.victimArchetype];
    row.appendChild(killer);
    row.appendChild(arrow);
    row.appendChild(victim);
    this.root.appendChild(row);
    const timer = setTimeout(() => this.removeRow(row), FADE_AFTER_MS);
    this.rows.push({ el: row, timer });
    while (this.rows.length > MAX_ROWS) {
      const oldest = this.rows.shift();
      if (oldest !== undefined) this.removeRow(oldest.el, oldest.timer);
    }
  }

  clear(): void {
    for (const r of this.rows) {
      clearTimeout(r.timer);
      r.el.remove();
    }
    this.rows.length = 0;
  }

  hide(): void {
    if (this.root !== null) this.root.style.display = 'none';
  }

  show(): void {
    if (this.root !== null) this.root.style.display = 'flex';
  }

  dispose(): void {
    this.clear();
    if (this.root !== null) this.root.remove();
    this.root = null;
  }

  private removeRow(el: HTMLElement, timer?: ReturnType<typeof setTimeout>): void {
    if (timer !== undefined) clearTimeout(timer);
    el.classList.add('kill-feed__row--out');
    setTimeout(() => {
      el.remove();
      const idx = this.rows.findIndex((r) => r.el === el);
      if (idx !== -1) this.rows.splice(idx, 1);
    }, 240);
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  top: 68px;
  right: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 24;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  pointer-events: none;
  max-width: 260px;
}
.kill-feed__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  background: rgba(12, 14, 18, 0.78);
  border-left: 3px solid currentColor;
  border-radius: 2px;
  font-size: 12px;
  letter-spacing: 0.04em;
  animation: kill-feed-in 180ms ease-out;
  text-shadow: 0 0 4px currentColor;
}
.kill-feed__row--out { animation: kill-feed-out 220ms ease-in forwards; }
.kill-feed__row--ally  { color: #6fb8ff; }
.kill-feed__row--enemy { color: #ff8c64; }
.kill-feed__name { color: #eef3f8; }
.kill-feed__arrow { color: currentColor; font-size: 10px; }
@keyframes kill-feed-in {
  0%   { transform: translateX(20px); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
}
@keyframes kill-feed-out {
  0%   { opacity: 1; }
  100% { transform: translateX(20px); opacity: 0; }
}
/* Mobile: shift down so it doesn't collide with the critical-flip stack. */
@media (max-width: 820px), (pointer: coarse) {
  #${ROOT_ID} {
    top: 130px;
    right: 8px;
    max-width: 200px;
  }
  .kill-feed__row { font-size: 10px; padding: 3px 8px; }
}
`;
  document.head.appendChild(style);
}
